import React, { useMemo } from "react";
import {
  Panel,
  PanelHeader,
  PanelContent,
  PanelFooter,
  PanelFooterColumn,
  Button,
  Heading,
} from "@flixbus/honeycomb-react";
import {
  Icon,
  IconCalculator,
  IconInfoSolid,
  IconQuestionSolid,
} from "@flixbus/honeycomb-icons-react";
import type { CostPanelProps, FormulaVariable } from "../CostPanel.types";
import { panelId } from "../CostPanel.constants";
import BucketSelector from "../../BucketSelector/BucketSelector.component";
import InputsBody, { formId } from "./InputsBody/InputsBody.component";
import styles from "../CostPanel.module.scss";

const AddCost: React.FC<CostPanelProps> = ({
  active,
  onClose,
  planningMonths,
  formulaVariables,
  formulaExpression,
  referencedOpDataVariables,
  line,
  defaultFormValues,
  activeSubcategoryName,
  dimensionValues,
  activeDimensionValue,
  dimensionEditState,
  onDimensionValueChange,
  onToggleFormulaMode,
  onSubmit,
  onEnterEditDimensionValues,
  isSaving,
}) => {
  const panelTitle = activeSubcategoryName
    ? `Add Cost · ${activeSubcategoryName}`
    : "Add Cost";

  const hasDimensionType = dimensionEditState !== null;
  const showBucketSelector = hasDimensionType || dimensionValues.length > 0;
  const hasNoDimensionValues = hasDimensionType && dimensionValues.length === 0;

  const { perMonthVars, singleVars } = useMemo(() => {
    const perMonth: FormulaVariable[] = [];
    const single: FormulaVariable[] = [];
    for (const v of formulaVariables) {
      if (v.inputType === "PER_MONTH") perMonth.push(v);
      else single.push(v);
    }
    return { perMonthVars: perMonth, singleVars: single };
  }, [formulaVariables]);

  return (
    <Panel id={panelId} active={active} extraClasses={styles.panelWrapper}>
      <PanelHeader closeButtonProps={{ label: "Close", onClick: onClose }}>
        <div className={styles.headerContent}>
          <div className={styles.headerLeft}>
            <Heading
              size={3}
              sectionHeader
              flushSpace
              extraClasses={styles.headerTitle}
            >
              {panelTitle}
            </Heading>
          </div>
          <div className={styles.headerIcons}>
            <Button display="square" appearance="link" aria-label="Info">
              <Icon InlineIcon={IconInfoSolid} aria-hidden="true" />
            </Button>
            <Button display="square" appearance="link" aria-label="Help">
              <Icon InlineIcon={IconQuestionSolid} aria-hidden="true" />
            </Button>
            <Button
              display="square"
              appearance="secondary"
              aria-label="Show formula"
              onClick={onToggleFormulaMode}
              disabled={hasNoDimensionValues}
            >
              <Icon InlineIcon={IconCalculator} aria-hidden="true" />
            </Button>
          </div>
        </div>
      </PanelHeader>
      <PanelContent>
        {showBucketSelector && (
          <div className={styles.navSection}>
            <BucketSelector
              dimensionTypeName={dimensionEditState?.dimensionTypeName}
              dimensionValues={dimensionValues}
              activeDimensionValue={activeDimensionValue}
              onChange={onDimensionValueChange}
              onEditDimensionValues={onEnterEditDimensionValues}
            />
          </div>
        )}
        {active && (
          <InputsBody
            formulaExpression={formulaExpression}
            formulaVariables={formulaVariables}
            referencedOpDataVariables={referencedOpDataVariables}
            line={line}
            perMonthVars={perMonthVars}
            singleVars={singleVars}
            planningMonths={planningMonths}
            defaultFormValues={defaultFormValues}
            onSubmit={onSubmit}
            onToggleFormulaMode={onToggleFormulaMode}
            noDimensionValues={hasNoDimensionValues}
            dimensionTypeName={dimensionEditState?.dimensionTypeName}
            onEnterEditDimensionValues={onEnterEditDimensionValues}
          />
        )}
      </PanelContent>
      <PanelFooter extraClasses={styles.footer}>
        <PanelFooterColumn>
          <Button
            appearance="primary"
            display="block"
            type="submit"
            form={formId}
            loading={isSaving}
            disabled={isSaving || hasNoDimensionValues}
          >
            Update
          </Button>
        </PanelFooterColumn>
      </PanelFooter>
    </Panel>
  );
};

export default AddCost;
